/**
 * Authentication and authorization utility functions
 */

import { User, BusinessSummary } from '../contexts/AuthTypes';

/**
 * User type enum matching the backend UserType enum
 */
export enum UserType {
  Regular = 0,
  Admin = 1,
  SuperAdmin = 2
}

/**
 * Check if a user has admin privileges
 * @param user - The user object to check
 * @returns true if the user is an admin or super admin, false otherwise
 */
export const isAdmin = (user: User | null): boolean => {
  if (!user) {
    return false;
  }
  
  return user.userType === UserType.Admin || user.userType === UserType.SuperAdmin;
};

/**
 * Check if a user has super admin privileges
 * @param user - The user object to check
 * @returns true if the user is a super admin, false otherwise
 */
export const isSuperAdmin = (user: User | null): boolean => {
  if (!user) {
    return false;
  }
  
  return user.userType === UserType.SuperAdmin;
};

/**
 * Get user type name as a string
 * @param userType - The user type number
 * @returns The user type name as a string
 */
export const getUserTypeName = (userType: number): string => {
  switch (userType) {
    case UserType.Regular:
      return 'Regular';
    case UserType.Admin:
      return 'Admin';
    case UserType.SuperAdmin:
      return 'Super Admin';
    default:
      return 'Unknown';
  }
};

/**
 * Check if a user has the required permission level
 * @param user - The user object to check
 * @param requiredLevel - The minimum required user type level
 * @returns true if the user meets the required level, false otherwise
 */
export const hasPermissionLevel = (user: User | null, requiredLevel: UserType): boolean => {
  if (!user) {
    return false;
  }

  return user.userType >= requiredLevel;
};

/**
 * Check whether a business is on the Exclusive plan (the only plan that
 * receives dispatch emails / monthly reports). Case-insensitive.
 * @param business - The business summary to check
 * @returns true if the business's planName is "exclusive"
 */
export const isExclusive = (business: BusinessSummary | null): boolean => {
  if (!business) {
    return false;
  }

  return (business.planName ?? '').toLowerCase() === 'exclusive';
};